// Compras (docs/108 B6): contratos de lectura y la acción común. Las filas vienen tal como las entrega
// la API (snake_case de la tabla); las cifras, como TEXTO decimal. Claves bajo ['emp', e, 'compras', …].
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';

type Decimal = string;

export interface Pagina<T> {
  items: T[];
  total?: number;
  pagina: number;
  tamano: number;
}

export interface Transicion {
  id: string;
  accion: string;
  estado_desde: string | null;
  estado_hasta: string | null;
  motivo: string | null;
  actor: string | null;
  en: string;
}

// ---------------------------------------------------------------- orden de compra

export type EstadoOrden =
  'BORRADOR' | 'EN_APROBACION' | 'APROBADA' | 'EMITIDA' | 'CERRADA' | 'CANCELADA';
export const NOMBRE_ESTADO_OC: Record<EstadoOrden, string> = {
  BORRADOR: 'Borrador',
  EN_APROBACION: 'En aprobación',
  APROBADA: 'Aprobada',
  EMITIDA: 'Emitida',
  CERRADA: 'Cerrada',
  CANCELADA: 'Cancelada',
};
export const CONDICIONES_PAGO = ['contado', 'credito', 'cheque', 'transferencia', 'otro'] as const;

export interface OrdenListada {
  id: string;
  numero: string;
  numero_visible: string;
  estado: EstadoOrden;
  fecha_negocio: string;
  fecha_entrega_esperada: string | null;
  proveedor_entidad_id: string;
  proveedor_razon_social: string;
  proveedor_rut: string;
  ubicacion_destino_id: string;
  condicion_pago: string;
  plazo_dias: number;
  observaciones: string | null;
  version: number;
  revision: number;
  total_neto: Decimal;
}

export interface LineaOc {
  id: string;
  numero: number;
  productoId: string;
  presentacionId: string;
  claseLinea: 'MERCADERIA' | 'SERVICIO';
  sku: string;
  descripcion: string;
  presentacionCodigo: string;
  unidadCodigo: string;
  factorABase: Decimal;
  cantidad: Decimal;
  cantidadBase: Decimal;
  precioUnitario: Decimal;
  descuento: Decimal;
  neto: Decimal;
  impuestos: Array<{ codigo: string; tipo: string; tasa: Decimal; monto: Decimal }>;
}

export interface OrdenLeida {
  orden: Omit<OrdenListada, 'numero_visible' | 'total_neto'> & {
    creado_en: string;
    creado_por: string;
    modo_aprobacion: string | null;
  };
  numeroVisible: string;
  lineas: LineaOc[];
  totales: {
    neto: Decimal;
    impuestos: Array<{ codigo: string; monto: Decimal }>;
    totalImpuestos: Decimal;
    total: Decimal;
  };
  cumplimiento: {
    estado: 'SIN_RECEPCION' | 'PARCIAL' | 'COMPLETA' | 'SOBRE_RECIBIDA';
    recepcionesVigentes: number;
    recepcionesReversadas: number;
    lineas: Array<{
      numero: number;
      productoId: string;
      ordenadaBase: Decimal;
      recibidaBase: Decimal;
      estado: string | null;
    }>;
  };
  solicitudes: Array<{
    id: string;
    estado: string;
    solicitada_por: string;
    resuelta_por: string | null;
    motivo_resolucion: string | null;
    creado_en: string;
  }>;
  transiciones: Transicion[];
}

export const NOMBRE_CUMPLIMIENTO: Record<string, string> = {
  SIN_RECEPCION: 'Sin recepción',
  PARCIAL: 'Recibida parcial',
  COMPLETA: 'Recibida completa',
  SOBRE_RECIBIDA: 'Sobre-recibida',
};

// ---------------------------------------------------------------- recepción

export type EstadoRecepcion = 'BORRADOR' | 'CONFIRMADA' | 'DESCARTADA' | 'REVERSADA';
export const NOMBRE_ESTADO_RECEPCION: Record<EstadoRecepcion, string> = {
  BORRADOR: 'Borrador',
  CONFIRMADA: 'Confirmada',
  DESCARTADA: 'Descartada',
  REVERSADA: 'Reversada',
};

export interface RecepcionListada {
  id: string;
  modalidad: 'CONTRA_OC' | 'SIN_OC';
  orden_id: string | null;
  proveedor_entidad_id: string;
  ubicacion_id: string;
  fecha_negocio: string;
  estado: EstadoRecepcion;
  version: number;
  numero: string | null;
  numero_visible: string | null;
  confirmada_en: string | null;
}

export interface LineaRecibida {
  id: string;
  numero: number;
  orden_linea_numero: number | null;
  producto_id: string;
  presentacion_id: string;
  sku: string;
  descripcion: string;
  presentacion_codigo: string;
  unidad_codigo: string;
  factor_a_base: Decimal;
  cantidad_recibida: Decimal;
  cantidad_rechazada: Decimal;
  cantidad_aceptada: Decimal;
  cantidad_aceptada_base: Decimal;
  precio_oc: Decimal | null;
  motivo_rechazo: string | null;
}

export interface RecepcionLeida {
  borrador: {
    id: string;
    empresa_id: string;
    modalidad: 'CONTRA_OC' | 'SIN_OC';
    orden_id: string | null;
    proveedor_entidad_id: string;
    ubicacion_id: string;
    fecha_negocio: string;
    guia_tipo: string | null;
    guia_folio: string | null;
    motivo_sin_oc: string | null;
    observaciones: string | null;
    estado: string;
    version: number;
  };
  estado: EstadoRecepcion;
  numeroVisible: string | null;
  lineasBorrador: Array<{
    id: string;
    numero: number;
    orden_linea_numero: number | null;
    producto_id: string | null;
    presentacion_id: string | null;
    cantidad_recibida: Decimal;
    cantidad_rechazada: Decimal;
    motivo_rechazo: string | null;
  }>;
  recepcion: {
    id: string;
    numero: string;
    proveedor_razon_social: string;
    proveedor_rut: string;
    confirmada_en: string;
  } | null;
  lineas: LineaRecibida[];
  reversa: { id: string; motivo?: string } | null;
  excepciones: Array<{
    id: string;
    tipo: string;
    estado: string;
    version: number;
    detalle: unknown;
  }>;
  transiciones: Transicion[];
  solicitudesReversa: Array<{
    id: string;
    estado: string;
    motivo: string;
    version: number;
    solicitada_por: string;
  }>;
  facturacion: Array<{
    recepcionLineaId: string;
    aceptadaBase: Decimal;
    facturadaBase: Decimal;
    estado: 'SIN_FACTURA' | 'PARCIAL' | 'COMPLETA';
  }>;
}

// ---------------------------------------------------------------- documento del proveedor

export type EstadoDocumento = 'REGISTRADA' | 'ACEPTADA' | 'RECHAZADA';
export const NOMBRE_ESTADO_DOC: Record<EstadoDocumento, string> = {
  REGISTRADA: 'Registrada',
  ACEPTADA: 'Aceptada',
  RECHAZADA: 'Rechazada',
};
export const NOMBRE_TIPO_COMPRA: Record<number, string> = {
  30: 'Factura (papel)',
  32: 'Factura exenta (papel)',
  33: 'Factura electrónica',
  34: 'Factura exenta electrónica',
  46: 'Factura de compra',
  55: 'Nota de débito (papel)',
  56: 'Nota de débito electrónica',
  60: 'Nota de crédito (papel)',
  61: 'Nota de crédito electrónica',
};

export interface DocumentoListado {
  id: string;
  clase_documento?: 'FACTURA' | 'NOTA_CREDITO' | 'NOTA_DEBITO';
  tipo_sii: number | null;
  folio: string | null;
  /** Documento no tributario: su clase (OTRO = ingreso sin factura) y su número. */
  clase_externa?: string | null;
  numero_externo?: string | null;
  rut_emisor: string;
  proveedor_entidad_id: string | null;
  razon_social_emisor: string;
  fecha_emision: string;
  monto_total: Decimal;
  estado: EstadoDocumento;
  version: number;
  medicion?: number | null;
  numero_visible?: string;
  /** 0131: invalidado (el ingreso sin factura anulado o corregido): ya no se debe. */
  invalidada?: boolean;
  clase_identidad?: string;
}

export interface LineaDocumento {
  id: string;
  numero: number;
  descripcion: string;
  codigo_proveedor: string | null;
  producto_id: string | null;
  presentacion_id: string | null;
  sku: string | null;
  presentacion_codigo?: string | null;
  unidad?: string | null;
  factor_a_base?: Decimal | null;
  cantidad: Decimal;
  precio_unitario: Decimal;
  descuento: Decimal;
  neto: Decimal;
  /** Impuestos de la línea (el ILA imputado a su producto). */
  impuestos?: Array<{ impuesto_codigo: string; tipo: string; monto: Decimal }>;
  orden_id: string | null;
  orden_linea_numero: number | null;
  identificacion?: { producto_id: string; sku: string } | null;
  sugerencia?: { productoId: string; presentacionId: string } | null;
  asignadoDocumento?: Decimal;
  facturacion?: 'SIN_ASIGNAR' | 'PARCIAL' | 'TOTAL';
}

export interface Diferencia {
  id: string;
  numero: number;
  tipo: string;
  clasificacion: string;
  esperado: Decimal | null;
  documentado: Decimal | null;
  delta: Decimal | null;
  monto: Decimal | null;
  estado: string;
  documento_linea_id: string | null;
}

export interface FacturaLeida {
  documento: DocumentoListado & {
    clase_documento: 'FACTURA' | 'NOTA_CREDITO' | 'NOTA_DEBITO';
    rut_receptor: string | null;
    fecha_recepcion: string | null;
    monto_neto: Decimal;
    monto_exento: Decimal;
    monto_iva: Decimal;
    monto_impuestos_adicionales: Decimal;
    motivo_rechazo: string | null;
    origen_captura: string;
    clase_identidad: string;
  };
  numeroVisible: string;
  lineas: LineaDocumento[];
  cargos: Array<{ numero: number; tipo: string; descripcion: string; monto: Decimal }>;
  impuestos: Array<{ impuesto_codigo: string; tipo: string; monto: Decimal }>;
  referencias: Array<{
    tipo: string;
    orden_id: string | null;
    recepcion_id: string | null;
    folio_texto: string | null;
    razon?: string | null;
  }>;
  validacionTotales: unknown;
  asignaciones: Array<{
    id: string;
    documento_linea_id: string;
    recepcion_id: string;
    recepcion_linea_id: string;
    cantidad_documento: Decimal;
    cantidad_base: Decimal;
    reversada: boolean;
  }>;
  conciliacion: {
    estado: string;
    medicion: number;
    situacion: 'SIN_MEDIR' | 'CUADRADA' | 'CON_DIFERENCIAS_ABIERTAS';
    version: number;
    diferencias: Diferencia[];
  } | null;
  transiciones: Transicion[];
  /** 0131: el ingreso sin factura anulado (o corregido): quién, cuándo y por qué. */
  invalidacion?: {
    en: string;
    motivo: string;
    unaFirma: boolean;
    solicitadaPor: string;
    aprobadaPor: string;
  } | null;
  /** El ingreso corregido que lo reemplazó (mismo número) y el anulado al que corrige. */
  reemplazadoPor?: string | null;
  corrigeA?: string | null;
}

export interface NotaLeida {
  documento: FacturaLeida['documento'];
  numeroVisible: string;
  clase: 'NOTA_CREDITO' | 'NOTA_DEBITO';
  lineas: LineaDocumento[];
  impuestos: FacturaLeida['impuestos'];
  referencias: FacturaLeida['referencias'];
  imputaciones: Array<{
    id: string;
    numero: number;
    efecto: string;
    factura_id: string | null;
    monto_neto: Decimal;
    motivo: string;
  }>;
  situacion: 'SIN_IMPUTAR' | 'CONCILIADA' | 'PENDIENTE_CONCILIACION';
  sinImputar: Decimal;
  transiciones: Transicion[];
}

export const EFECTOS_NOTA = [
  'DEVOLUCION',
  'PRECIO',
  'DESCUENTO',
  'FLETE',
  'TRIBUTARIA',
  'FINANCIERA',
] as const;

// ---------------------------------------------------------------- devolución a proveedor

export type EstadoDevolucion =
  'BORRADOR' | 'PENDIENTE_APROBACION' | 'APROBADA' | 'CONFIRMADA' | 'CANCELADA' | 'REVERSADA';
export const NOMBRE_ESTADO_DEVOLUCION: Record<EstadoDevolucion, string> = {
  BORRADOR: 'Borrador',
  PENDIENTE_APROBACION: 'Por aprobar',
  APROBADA: 'Aprobada',
  CONFIRMADA: 'Confirmada',
  CANCELADA: 'Cancelada',
  REVERSADA: 'Reversada',
};

export interface DevolucionListada {
  id: string;
  numero: string | null;
  recepcion_id: string;
  proveedor_entidad_id: string;
  ubicacion_id: string;
  fecha_negocio: string;
  estado: EstadoDevolucion;
  motivo: string;
  version: number;
  creado_en: string;
}

export interface DevolucionLeida {
  devolucion: DevolucionListada;
  estado: EstadoDevolucion;
  numeroVisible: string | null;
  lineas: Array<{
    id: string;
    numero: number;
    recepcion_linea_id: string;
    producto_id: string;
    presentacion_id: string;
    cantidad: Decimal;
    cantidad_base: Decimal;
  }>;
  acreditacion: {
    estado: 'ESPERANDO_NC' | 'PARCIAL' | 'ACREDITADA';
    devueltaBase: Decimal;
    acreditadaBase: Decimal;
  };
  solicitudes: Array<{ id: string; estado: string; tipo: string; motivo: string }>;
  transiciones: Transicion[];
}

// ---------------------------------------------------------------- costos

export interface CompraDeProducto {
  documentoId: string;
  tipoSii: number | null;
  folio: string | null;
  fechaEmision: string;
  proveedorEntidadId: string | null;
  proveedor: string;
  productoId: string;
  sku: string | null;
  descripcion: string;
  cantidad: Decimal;
  factorABase: Decimal;
  precioUnitario: Decimal;
  costoBase: Decimal;
}
export interface CostoDeProducto {
  productoId: string;
  sku: string | null;
  descripcion: string;
  compras: number;
  ultima: CompraDeProducto;
  anterior: CompraDeProducto | null;
  costoMinimo: Decimal;
  costoMaximo: Decimal;
}

// ---------------------------------------------------------------- bandejas de aprobación

export interface SolicitudCorreccion {
  id: string;
  tipo: 'REVERSA_DEVOLUCION' | 'INVALIDACION_FACTURA';
  objeto_id: string;
  estado: string;
  motivo: string;
  solicitada_por: string;
  version: number;
  creado_en: string;
}
export interface SolicitudReversa {
  id: string;
  recepcion_id: string;
  estado: string;
  motivo: string;
  solicitada_por: string;
  version: number;
  creado_en: string;
}
export interface ExcepcionRecepcion {
  id: string;
  recepcion_id: string;
  tipo: string;
  estado: string;
  detalle: unknown;
  version: number;
  creado_en: string;
}

// ---------------------------------------------------------------- consultas

export const clavesCompras = {
  todo: (e: string) => ['emp', e, 'compras'] as const,
  ordenes: (e: string, f: object) => ['emp', e, 'compras', 'ordenes', f] as const,
  orden: (e: string, id: string) => ['emp', e, 'compras', 'orden', id] as const,
  recepciones: (e: string, f: object) => ['emp', e, 'compras', 'recepciones', f] as const,
  recepcion: (e: string, id: string) => ['emp', e, 'compras', 'recepcion', id] as const,
  facturas: (e: string, f: object) => ['emp', e, 'compras', 'facturas', f] as const,
  factura: (e: string, id: string) => ['emp', e, 'compras', 'factura', id] as const,
  notas: (e: string, f: object) => ['emp', e, 'compras', 'notas', f] as const,
  nota: (e: string, id: string) => ['emp', e, 'compras', 'nota', id] as const,
  devoluciones: (e: string, f: object) => ['emp', e, 'compras', 'devoluciones', f] as const,
  devolucion: (e: string, id: string) => ['emp', e, 'compras', 'devolucion', id] as const,
  costos: (e: string, f: object) => ['emp', e, 'compras', 'costos', f] as const,
  bandeja: (e: string, que: string) => ['emp', e, 'compras', 'bandeja', que] as const,
  recibidos: (e: string, f: object) => ['emp', e, 'compras', 'recibidos', f] as const,
  recibido: (e: string, id: string) => ['emp', e, 'compras', 'recibido', id] as const,
};

const limpio = (f: Record<string, string | number | undefined>) =>
  Object.fromEntries(Object.entries(f).filter(([, v]) => v !== undefined && v !== ''));

export interface FiltroOrdenes {
  estado?: EstadoOrden | undefined;
  texto?: string | undefined;
  proveedorEntidadId?: string | undefined;
  pagina?: number;
}
export function useOrdenesCompra(empresaId: string, f: FiltroOrdenes) {
  return useQuery({
    queryKey: clavesCompras.ordenes(empresaId, f),
    queryFn: () =>
      api.get<Pagina<OrdenListada>>('/compras/ordenes', {
        query: limpio({
          empresaId,
          estado: f.estado,
          texto: f.texto && f.texto.trim().length >= 2 ? f.texto.trim() : undefined,
          proveedorEntidadId: f.proveedorEntidadId,
          pagina: f.pagina ?? 1,
          tamano: 50,
        }),
      }),
  });
}

export function useOrdenCompra(empresaId: string, id: string | null) {
  return useQuery({
    queryKey: clavesCompras.orden(empresaId, id ?? ''),
    queryFn: () => api.get<OrdenLeida>(`/compras/ordenes/${id}`),
    enabled: !!id,
  });
}

export function useRecepciones(
  empresaId: string,
  f: {
    estado?: EstadoRecepcion | undefined;
    ordenId?: string | undefined;
    proveedorEntidadId?: string | undefined;
    pagina?: number;
  },
) {
  return useQuery({
    queryKey: clavesCompras.recepciones(empresaId, f),
    queryFn: () =>
      api.get<Pagina<RecepcionListada>>('/compras/recepciones', {
        query: limpio({
          empresaId,
          estado: f.estado,
          ordenId: f.ordenId,
          proveedorEntidadId: f.proveedorEntidadId,
          pagina: f.pagina ?? 1,
          tamano: 50,
        }),
      }),
  });
}

export function useRecepcion(empresaId: string, id: string | null) {
  return useQuery({
    queryKey: clavesCompras.recepcion(empresaId, id ?? ''),
    queryFn: () => api.get<RecepcionLeida>(`/compras/recepciones/${id}`),
    enabled: !!id,
  });
}

export function useFacturasCompra(
  empresaId: string,
  f: {
    estado?: EstadoDocumento | undefined;
    proveedorEntidadId?: string | undefined;
    folio?: string | undefined;
    pagina?: number;
  },
) {
  return useQuery({
    queryKey: clavesCompras.facturas(empresaId, f),
    queryFn: () =>
      api.get<Pagina<DocumentoListado>>('/compras/facturas', {
        query: limpio({
          empresaId,
          estado: f.estado,
          proveedorEntidadId: f.proveedorEntidadId,
          folio: f.folio && /^\d+$/.test(f.folio) ? f.folio : undefined,
          pagina: f.pagina ?? 1,
          tamano: 50,
        }),
      }),
  });
}

export function useFacturaCompra(empresaId: string, id: string | null) {
  return useQuery({
    queryKey: clavesCompras.factura(empresaId, id ?? ''),
    queryFn: () => api.get<FacturaLeida>(`/compras/facturas/${id}`),
    enabled: !!id,
  });
}

export function useNotasCompra(
  empresaId: string,
  f: {
    clase?: 'NOTA_CREDITO' | 'NOTA_DEBITO' | undefined;
    estado?: EstadoDocumento | undefined;
    proveedorEntidadId?: string | undefined;
  },
) {
  return useQuery({
    queryKey: clavesCompras.notas(empresaId, f),
    queryFn: () =>
      api.get<Pagina<DocumentoListado>>('/compras/notas', {
        query: limpio({
          empresaId,
          clase: f.clase,
          estado: f.estado,
          proveedorEntidadId: f.proveedorEntidadId,
          tamano: 100,
        }),
      }),
  });
}

export function useNotaCompra(empresaId: string, id: string | null) {
  return useQuery({
    queryKey: clavesCompras.nota(empresaId, id ?? ''),
    queryFn: () => api.get<NotaLeida>(`/compras/notas/${id}`),
    enabled: !!id,
  });
}

export function useDevoluciones(
  empresaId: string,
  f: { estado?: EstadoDevolucion | undefined; recepcionId?: string | undefined },
) {
  return useQuery({
    queryKey: clavesCompras.devoluciones(empresaId, f),
    queryFn: () =>
      api.get<Pagina<DevolucionListada>>('/compras/devoluciones', {
        query: limpio({
          empresaId,
          // REVERSADA no es una columna: se lista por CONFIRMADA y se distingue al abrir.
          estado: f.estado === 'REVERSADA' ? undefined : f.estado,
          recepcionId: f.recepcionId,
          tamano: 100,
        }),
      }),
  });
}

export function useDevolucion(empresaId: string, id: string | null) {
  return useQuery({
    queryKey: clavesCompras.devolucion(empresaId, id ?? ''),
    queryFn: () => api.get<DevolucionLeida>(`/compras/devoluciones/${id}`),
    enabled: !!id,
  });
}

export function useCostosCompra(
  empresaId: string,
  f: { texto?: string | undefined; proveedorEntidadId?: string | undefined },
) {
  return useQuery({
    queryKey: clavesCompras.costos(empresaId, f),
    queryFn: () =>
      api.get<CostoDeProducto[]>('/compras/costos', {
        query: limpio({
          empresaId,
          texto: f.texto && f.texto.trim().length >= 2 ? f.texto.trim() : undefined,
          proveedorEntidadId: f.proveedorEntidadId,
        }),
      }),
  });
}

export function useHistorialCosto(empresaId: string, productoId: string | null) {
  return useQuery({
    queryKey: clavesCompras.costos(empresaId, { productoId }),
    queryFn: () =>
      api.get<CompraDeProducto[]>('/compras/costos', { query: { empresaId, productoId } }),
    enabled: !!productoId,
  });
}

export function useBandejaCompras<T>(empresaId: string, ruta: string, habilitado = true) {
  return useQuery({
    queryKey: clavesCompras.bandeja(empresaId, ruta),
    queryFn: () => api.get<T[]>(ruta, { query: { empresaId, estado: 'PENDIENTE' } }),
    enabled: habilitado,
  });
}

/** Nombre de una entidad (proveedor) por id: se pide una vez y queda en caché. */
export function useNombreEntidad(id: string | null | undefined) {
  return useQuery({
    queryKey: ['entidad-nombre', id],
    queryFn: () =>
      api.get<{ entidad: { razon_social: string; rut: string | null; dv: string | null } }>(
        `/entidades/${id}`,
      ),
    enabled: !!id,
    staleTime: 10 * 60_000,
    select: (d) => d.entidad,
  });
}

export interface AccionCompras {
  ruta: string;
  cuerpo: unknown;
  metodo?: 'POST' | 'PATCH';
}

/**
 * La acción común de Compras: POST/PATCH con clave de idempotencia por INTENTO (la API la exige) y,
 * al terminar, se refresca todo Compras (y la CxP, que se deriva de lo aceptado).
 */
export function useAccionCompras(empresaId: string) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  return useMutation<unknown, Error, AccionCompras>({
    mutationFn: ({ ruta, cuerpo, metodo = 'POST' }) => {
      const idempotencia = intentos.clave(`compras:${metodo}:${ruta}`, cuerpo);
      return metodo === 'PATCH'
        ? api.patch<unknown>(ruta, cuerpo, { idempotencia })
        : api.post<unknown>(ruta, cuerpo, { idempotencia });
    },
    onSuccess: async () => {
      intentos.cerrar();
      await Promise.all([
        qc.invalidateQueries({ queryKey: clavesCompras.todo(empresaId) }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'pagos'] }),
      ]);
    },
    onError: (e) => intentos.cerrar(e),
  });
}

// ================================================================ facturas recibidas por XML

export type EstadoRecibido = 'PENDIENTE' | 'REGISTRADO' | 'DESCARTADO';

export interface RecibidoListado {
  id: string;
  rut_emisor: string;
  razon_social_emisor: string;
  tipo_sii: number;
  folio: string;
  fecha_emision: string;
  monto_total: string;
  firma_valida: boolean;
  timbre_valido: boolean;
  origen: 'CORREO' | 'ARCHIVO';
  estado: EstadoRecibido;
  documento_id: string | null;
  motivo: string | null;
  recibido_en: string;
  version: number;
  lineas: number;
  /** 0111: ya entró al stock con su guía (falta registrar la factura). */
  recepcion_id?: string | null;
}

export interface LineaRecibidaXml {
  numero: number;
  codigos: Array<{ tipo: string; valor: string }>;
  descripcion: string;
  detalle: string | null;
  cantidad: string | null;
  unidad: string | null;
  precio: string | null;
  descuento: number;
  recargo: number;
  codigoImpuesto: string | null;
  exenta: boolean;
  monto: number;
}

export interface SugerenciaRecibido {
  productoId: string;
  presentacionId: string;
  fuente: 'HISTORIAL_CODIGO' | 'HISTORIAL_DESCRIPCION' | 'CATALOGO';
  sku: string | null;
  presentacion: {
    codigo: string;
    nombre: string;
    factorABase: string;
    productoNombre: string;
    sku: string;
  } | null;
}

export interface RecibidoLeido {
  recibido: {
    id: string;
    empresaId: string;
    estado: EstadoRecibido;
    version: number;
    origen: 'CORREO' | 'ARCHIVO';
    correoRemitente: string | null;
    correoAsunto: string | null;
    correoFecha: string | null;
    recibidoEn: string;
    documentoId: string | null;
    motivo: string | null;
    registrable: boolean;
    /** 0111: la guía con que ya entró al stock (la factura se registra después). */
    recepcionId: string | null;
    guia: {
      lineas: Array<
        | { numero: number; productoId: string; presentacionId: string }
        | { numero: number; como: 'FLETE' | 'SIN_PRODUCTO' }
      >;
      recibidas: Array<{ numero: number; recepcionLineaId: string }>;
    } | null;
  };
  lectura: {
    tipoDte: number;
    folio: number;
    fechaEmision: string;
    fechaVencimiento: string | null;
    lineasConIva: boolean;
    emisor: { rut: string; razonSocial: string; giro: string | null };
    receptor: { rut: string; razonSocial: string | null };
    totales: {
      neto: number;
      exento: number;
      iva: number;
      tasaIva: string | null;
      impuestos: Array<{ codigo: string; tasa: string | null; monto: number }>;
      total: number;
    };
    lineas: LineaRecibidaXml[];
    referencias: Array<{
      tipo: string;
      folio: string | null;
      fecha: string | null;
      razon: string | null;
    }>;
    firma: { valida: boolean; motivo: string | null; firmante: string | null };
    timbre: { valido: boolean; motivo: string | null };
  };
  proveedor: { entidadId: string; razonSocial: string; bloqueado: boolean } | null;
  yaRegistrada: { id: string; estado: string; origen_captura: string } | null;
  lineas: Array<{
    numero: number;
    /** 2026-10-30: cómo viene la línea del XML (caja de N, unidad u otra). */
    unidadXml?: UnidadXml;
    sugerencia: SugerenciaRecibido | null;
  }>;
}

export interface UnidadXml {
  clase: 'CAJA' | 'UNIDAD' | 'OTRA';
  pack: number | null;
}

/** Lo mismo que valida el servidor (compras/recibidos.ts `choqueDeUnidad`): qué no calza, o null. */
export function choqueDeUnidad(x: UnidadXml | undefined, factor: number | null): string | null {
  if (!x || factor === null || !Number.isFinite(factor)) return null;
  if (x.clase === 'CAJA' && x.pack !== null && factor !== x.pack)
    return `viene en cajas de ${x.pack} y elegiste la presentación de ${factor}`;
  if (x.clase === 'CAJA' && x.pack === null && factor <= 1)
    return 'viene en cajas y elegiste la unidad';
  if (x.clase === 'UNIDAD' && factor !== 1)
    return `viene en unidades y elegiste la presentación de ${factor}`;
  return null;
}

export function useRecibidos(
  empresaId: string,
  f: { estado: EstadoRecibido | 'TODOS'; texto?: string | undefined },
) {
  return useQuery({
    queryKey: clavesCompras.recibidos(empresaId, f),
    queryFn: () =>
      api.get<{ filas: RecibidoListado[]; conteo: Partial<Record<EstadoRecibido, number>> }>(
        '/compras/recibidos',
        { query: limpio({ empresaId, estado: f.estado, texto: f.texto }) },
      ),
  });
}

export function useRecibido(empresaId: string, id: string) {
  return useQuery({
    queryKey: clavesCompras.recibido(empresaId, id),
    queryFn: () => api.get<RecibidoLeido>(`/compras/recibidos/${id}`),
  });
}
